import React, { useEffect, useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { passwordMeetsAllRules, EGYPTIAN_NATIONAL_ID_REGEX, EGYPTIAN_PHONE_REGEX, ACADEMIC_YEARS, ACADEMIC_YEAR_LABELS, type AcademicYear } from '@physioedge/shared';
import { api, ApiError } from '../api';
import { Button, Input, Alert, Card, PasswordInput, PasswordChecklist, PageLoader } from '../components/ui';
import { GoogleSignIn } from '../components/GoogleSignIn';
import { useSupportPhone } from '../components/WhatsAppHelp';
import { resetPendingApprovalPrompt, signedInHome, useAuth } from '../auth';
import { FormattedText } from '../lib/simpleMarkup';

export function RegisterPage() {
  const { me, loading: authLoading, refetch } = useAuth();
  const navigate = useNavigate();
  const publicSettingsQ = useSupportPhone();
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', password: '', confirmPassword: '',
    nationalId: '', university: '', academicYear: '', referralCode: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [referralStatus, setReferralStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid' | 'format'>('idle');
  const [referralHint, setReferralHint] = useState('');
  const [googleCredential, setGoogleCredential] = useState('');
  const [policyAccepted, setPolicyAccepted] = useState(false);

  const physioedgePolicy = publicSettingsQ.data?.physioedgePolicy
    ?? 'By creating an account, you agree to PhysioEDGE terms.';

  useEffect(() => {
    const raw = sessionStorage.getItem('physioedge.googlePrefill');
    if (!raw) return;
    sessionStorage.removeItem('physioedge.googlePrefill');
    try {
      const parsed = JSON.parse(raw) as { email?: string; fullName?: string; credential?: string };
      if (!parsed.email || !parsed.credential) return;
      setGoogleCredential(parsed.credential);
      setForm(f => ({
        ...f,
        email: parsed.email ?? f.email,
        fullName: f.fullName.trim() ? f.fullName : (parsed.fullName ?? ''),
      }));
      setEmailStatus('available');
    } catch {
      // ignore bad prefill
    }
  }, []);

  if (authLoading) return <PageLoader variant="form" />;
  if (me) return <Navigate to={signedInHome(me)} replace />;

  const setSelect = (field: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setForm(f => ({ ...f, [field]: value }));
    setFieldErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm(f => ({ ...f, [field]: value }));
    if (field === 'email') setEmailStatus('idle');
    if (field === 'referralCode') {
      setReferralStatus('idle');
      setReferralHint('');
    }
    setFieldErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const nationalIdError = form.nationalId.length > 0 && !EGYPTIAN_NATIONAL_ID_REGEX.test(form.nationalId.trim())
    ? 'Must be a 14-digit Egyptian National ID'
    : fieldErrors.nationalId;
  const phoneError = form.phone.length > 0 && !EGYPTIAN_PHONE_REGEX.test(form.phone.trim())
    ? 'Use an Egyptian mobile number like 01XXXXXXXXX'
    : fieldErrors.phone;

  const checkEmail = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email) {
      setEmailStatus('idle');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailStatus('invalid');
      return;
    }
    setEmailStatus('checking');
    try {
      const res = await api.get<{ available: boolean }>(
        `/api/auth/email-available?email=${encodeURIComponent(email)}`,
      );
      setEmailStatus(res.available ? 'available' : 'taken');
    } catch {
      setEmailStatus('idle');
    }
  };

  const checkReferralCode = async () => {
    const code = form.referralCode.trim().toLowerCase();
    if (!code) {
      setReferralStatus('idle');
      setReferralHint('');
      return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(code) || code.length < 2 || code.length > 40) {
      setReferralStatus('format');
      setReferralHint('Use letters, numbers, and hyphens only');
      return;
    }
    setReferralStatus('checking');
    setReferralHint('');
    try {
      const res = await api.get<{
        valid: boolean;
        referrerDisplayName: string | null;
        kind: 'student' | 'doctor' | null;
        message?: string;
      }>(`/api/auth/referral-code-check?code=${encodeURIComponent(code)}`);
      if (res.valid) {
        setReferralStatus('valid');
        const who = res.referrerDisplayName ?? 'a PhysioEDGE member';
        const kindLabel = res.kind === 'doctor' ? 'mentor' : 'friend';
        setReferralHint(`Code found — ${who} (${kindLabel})`);
      } else {
        setReferralStatus('invalid');
        setReferralHint(res.message ?? 'No matching referral code');
      }
    } catch {
      setReferralStatus('idle');
      setReferralHint('');
    }
  };

  const passwordsMatch = form.password.length === 0 || form.confirmPassword.length === 0
    ? true
    : form.password === form.confirmPassword;
  const passwordReady = passwordMeetsAllRules(form.password);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!policyAccepted) { setError('Accept the PhysioEDGE policy to create an account.'); return; }
    if (!file) { setError('University ID image is required.'); return; }
    if (!passwordReady) { setError('Password does not meet all requirements.'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    if (emailStatus === 'taken') { setError('This email is already registered.'); return; }
    if (!EGYPTIAN_PHONE_REGEX.test(form.phone.trim())) {
      setError('Phone must be an Egyptian mobile number (01XXXXXXXXX).');
      return;
    }
    if (!EGYPTIAN_NATIONAL_ID_REGEX.test(form.nationalId.trim())) {
      setError('National ID must be 14 digits (starts with 2 or 3).');
      return;
    }
    if (!ACADEMIC_YEARS.includes(form.academicYear as AcademicYear)) {
      setError('Choose your year of study.');
      return;
    }
    if (form.referralCode.trim()) {
      if (referralStatus === 'invalid' || referralStatus === 'format') {
        setError('That referral code is not valid. Fix it or leave it blank.');
        return;
      }
      if (referralStatus !== 'valid') {
        await checkReferralCode();
        // Re-read after check — status updates async; call API check inline
        try {
          const code = form.referralCode.trim().toLowerCase();
          const res = await api.get<{ valid: boolean; message?: string }>(
            `/api/auth/referral-code-check?code=${encodeURIComponent(code)}`,
          );
          if (!res.valid) {
            setReferralStatus('invalid');
            setReferralHint(res.message ?? 'No matching referral code');
            setError(res.message ?? 'That referral code is not valid. Fix it or leave it blank.');
            return;
          }
          setReferralStatus('valid');
        } catch {
          setError('Could not verify the referral code. Try again.');
          return;
        }
      }
    }
    setError(''); setFieldErrors({}); setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'confirmPassword') return;
        if (v) fd.append(k, v);
      });
      fd.append('universityIdFile', file);
      fd.append('acceptPolicy', 'true');
      if (googleCredential) fd.append('googleCredential', googleCredential);
      const created = await api.post<{ id: string; role?: string; approvalStatus?: string }>(
        '/api/auth/register',
        fd,
      );
      if (created.id) resetPendingApprovalPrompt(created.id);
      await refetch();
      navigate(signedInHome({ role: 'student' }), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const next: Record<string, string> = {};
        if (Array.isArray(err.details)) {
          for (const detail of err.details) {
            if (detail && typeof detail === 'object' && 'path' in detail && 'message' in detail) {
              next[String((detail as { path: string }).path)] = String((detail as { message: string }).message);
            }
          }
        }
        setFieldErrors(next);
        setError(err.friendlyMessage);
      } else {
        setError('Registration failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-lg p-8">
        <h1 className="font-heading text-2xl font-bold text-ink mb-1">Join us</h1>
        <p className="text-sm text-ink-soft mb-6">Registration is free.</p>
        {googleCredential && (
          <Alert type="success" className="mb-4">
            Google filled your name and email. Add phone, password, national ID, university, year, and your university ID image.
          </Alert>
        )}
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Input label="Full name" value={form.fullName} onChange={set('fullName')} required />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={set('email')}
              onBlur={checkEmail}
              required
              autoComplete="email"
              readOnly={Boolean(googleCredential)}
              error={emailStatus === 'invalid' ? 'Enter a valid email address' : undefined}
            />
            {emailStatus === 'checking' && (
              <p className="text-xs text-ink-faint mt-1">Checking email…</p>
            )}
            {emailStatus === 'taken' && (
              <p className="text-xs text-danger mt-1">
                This email is already registered.{' '}
                <Link to="/login" className="font-medium underline hover:text-sand-strong">
                  Log in
                </Link>
              </p>
            )}
            {emailStatus === 'available' && (
              <p className="text-xs text-success mt-1">Email is available</p>
            )}
          </div>
          <Input
            label="Phone"
            type="tel"
            value={form.phone}
            onChange={set('phone')}
            placeholder="01XXXXXXXXX"
            required
            error={phoneError}
          />
          <div className="col-span-2 sm:col-span-1">
            <PasswordInput
              label="Password"
              value={form.password}
              onChange={set('password')}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <PasswordInput
              label="Confirm password"
              value={form.confirmPassword}
              onChange={set('confirmPassword')}
              required
              autoComplete="new-password"
              error={form.confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : undefined}
            />
          </div>
          <PasswordChecklist password={form.password} />
          <div className="col-span-2">
            <Input
              label="National ID"
              value={form.nationalId}
              onChange={set('nationalId')}
              inputMode="numeric"
              maxLength={14}
              required
              error={nationalIdError}
            />
          </div>
          <Input label="University" value={form.university} onChange={set('university')} required />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-ink-soft" htmlFor="academic-year">Year</label>
            <select
              id="academic-year"
              value={form.academicYear}
              onChange={setSelect('academicYear')}
              required
              className="border border-line rounded-radius-sm px-3 py-3 text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-sand/40 focus:border-sand transition-colors min-h-[48px]"
            >
              <option value="" disabled>
                Select year
              </option>
              {ACADEMIC_YEARS.map(year => (
                <option key={year} value={year}>{ACADEMIC_YEAR_LABELS[year]}</option>
              ))}
            </select>
            {fieldErrors.academicYear && (
              <p className="text-xs text-danger mt-1">{fieldErrors.academicYear}</p>
            )}
          </div>
          <div className="col-span-2">
            <Input
              label="Referral code (optional)"
              value={form.referralCode}
              onChange={set('referralCode')}
              onBlur={() => { void checkReferralCode(); }}
              error={
                fieldErrors.referralCode
                || (referralStatus === 'format' || referralStatus === 'invalid' ? referralHint : undefined)
              }
            />
            {referralStatus === 'checking' && (
              <p className="text-xs text-ink-faint mt-1">Checking code…</p>
            )}
            {referralStatus === 'valid' && referralHint && (
              <p className="text-xs text-success mt-1">{referralHint}</p>
            )}
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-ink-soft block mb-1">University ID image *</label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
              className="text-sm text-ink-soft file:mr-3 file:py-1.5 file:px-3 file:rounded-radius-sm file:border-0 file:text-sm file:font-medium file:bg-teal-soft file:text-teal hover:file:bg-teal-soft/70 cursor-pointer"
              required
            />
          </div>
          <label className="col-span-2 flex items-start gap-3 text-sm text-ink cursor-pointer">
            <input
              type="checkbox"
              checked={policyAccepted}
              onChange={e => setPolicyAccepted(e.target.checked)}
              className="mt-1 rounded border-line"
              required
            />
            <span>
              <span className="font-medium">I agree to the PhysioEDGE policy.</span>
              <FormattedText text={physioedgePolicy} className="mt-1 text-xs text-ink-soft" />
            </span>
          </label>
          <div className="col-span-2">
            <Button
              type="submit"
              loading={loading}
              className="w-full"
              size="lg"
              disabled={
                !policyAccepted
                || emailStatus === 'taken'
                || referralStatus === 'invalid'
                || referralStatus === 'format'
                || (form.password.length > 0 && !passwordReady)
                || (form.confirmPassword.length > 0 && form.password !== form.confirmPassword)
                || (form.nationalId.length > 0 && !EGYPTIAN_NATIONAL_ID_REGEX.test(form.nationalId.trim()))
                || (form.phone.length > 0 && !EGYPTIAN_PHONE_REGEX.test(form.phone.trim()))
              }
            >
              Join us
            </Button>
          </div>
        </form>
        <GoogleSignIn
          onError={setError}
          onUnknownAccount={({ email, fullName, credential }) => {
            setGoogleCredential(credential);
            setForm(f => ({
              ...f,
              email,
              fullName: f.fullName.trim() ? f.fullName : fullName,
            }));
            setEmailStatus('available');
            setError('');
          }}
        />
        <p className="text-center text-sm text-ink-soft mt-6">
          Already with us?{' '}
          <Link to="/login" className="text-sand-strong font-medium hover:underline">Log in</Link>
        </p>
      </Card>
    </div>
  );
}

export function RegisterSuccessPage() {
  const { me, loading } = useAuth();
  if (loading) return <PageLoader variant="form" />;
  if (me) return <Navigate to={signedInHome(me)} replace />;

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Application submitted</h1>
        <p className="text-ink-soft mb-6">An admin still needs to approve your university ID before you can book internships.</p>
        <Link to="/login" className="text-sand-strong font-medium hover:underline">Go to log in</Link>
      </div>
    </div>
  );
}
